import { useEffect, useRef, useState } from "react";
import { API } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Shield, Smartphone, Mail, KeyRound, ArrowRight, Loader2, RefreshCw } from "lucide-react";

/**
 * MfaChallenge — shown after password login when user has MFA enabled.
 * Props:
 *   challenge: { mfa_method, mfa_token, email_hint, dev_otp? }
 *   onSuccess(user)
 *   onCancel()
 */
export const MfaChallenge = ({ challenge, onSuccess, onCancel }) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const [hint, setHint] = useState(challenge.email_hint);
  const [devOtp, setDevOtp] = useState(challenge.dev_otp || null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const readJson = async (res) => {
    // Resilient to environments (e.g., Emergent preview wrapper) that may
    // pre-consume the body stream — read as text once, then parse.
    const text = await res.text();
    try { return text ? JSON.parse(text) : {}; } catch { return {}; }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/mfa/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ mfa_token: challenge.mfa_token, code: code.trim() }),
      });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data?.detail || "Verification failed");
      onSuccess(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    setError("");
    try {
      const res = await fetch(`${API}/auth/mfa/resend-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mfa_token: challenge.mfa_token }),
      });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data?.detail || "Resend failed");
      setHint(data.email_hint || hint);
      if (data.dev_otp) setDevOtp(data.dev_otp);
      toast.success("New code sent");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setResending(false);
    }
  };

  const isTotp = challenge.mfa_method === "totp";

  return (
    <Card className="bg-white shadow-2xl border-0" data-testid="mfa-challenge-card">
      <CardContent className="p-7 space-y-5">
        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-[#FFD507]/20 mx-auto flex items-center justify-center mb-3">
            {isTotp ? <Smartphone className="w-7 h-7 text-[#1E3A8A]" /> : <Mail className="w-7 h-7 text-[#1E3A8A]" />}
          </div>
          <h2 className="text-2xl font-heading font-bold text-slate-900">Verify it's you</h2>
          <p className="text-sm text-slate-500 mt-1">
            {isTotp
              ? "Enter the 6-digit code from your authenticator app."
              : <>We emailed a 6-digit code to <span className="font-medium text-slate-700">{hint}</span>.</>}
          </p>
        </div>

        {devOtp && (
          <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs rounded p-2 text-center" data-testid="dev-otp-banner">
            <span className="font-bold">DEV / PREVIEW MODE</span> — your code is <span className="font-mono font-bold">{devOtp}</span> (SMTP not configured)
          </div>
        )}

        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm" data-testid="mfa-error">{error}</div>}

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-600">Verification code</Label>
            <Input
              ref={inputRef}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              maxLength={6}
              placeholder="••••••"
              className="input-light text-center text-2xl tracking-[0.5em] font-mono h-14"
              data-testid="mfa-code-input"
              autoComplete="one-time-code"
            />
          </div>
          <Button type="submit" disabled={loading || code.length !== 6} className="w-full btn-primary h-11" data-testid="mfa-verify-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Verify <ArrowRight className="w-4 h-4 ml-1" /></>)}
          </Button>
        </form>

        <div className="flex items-center justify-between text-xs text-slate-500">
          {!isTotp ? (
            <button onClick={resend} disabled={resending} className="inline-flex items-center gap-1 text-[#1E3A8A] hover:underline" data-testid="mfa-resend-btn">
              <RefreshCw className={`w-3 h-3 ${resending ? "animate-spin" : ""}`} /> Resend code
            </button>
          ) : <span />}
          <button onClick={onCancel} className="text-slate-500 hover:text-slate-700" data-testid="mfa-cancel-btn">Cancel</button>
        </div>
      </CardContent>
    </Card>
  );
};


/**
 * MfaForcedSetup — shown when admin/instructor logs in but hasn't enrolled MFA yet.
 * Method picker → setup → verify. On success, the backend issues a real session.
 * Props:
 *   setup: { mfa_token, role, email, name }
 *   onSuccess(user)
 *   onCancel()
 */
export const MfaForcedSetup = ({ setup, onSuccess, onCancel }) => {
  const [step, setStep] = useState("pick"); // pick | enrol | verify
  const [method, setMethod] = useState(null);
  const [secret, setSecret] = useState(null);
  const [qr, setQr] = useState(null);
  const [emailHint, setEmailHint] = useState(null);
  const [devOtp, setDevOtp] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const readJson = async (res) => {
    const text = await res.text();
    try { return text ? JSON.parse(text) : {}; } catch { return {}; }
  };

  const startSetup = async (chosen) => {
    setMethod(chosen);
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/mfa/setup/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ method: chosen, mfa_token: setup.mfa_token }),
      });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data?.detail || "Could not start MFA setup");
      if (chosen === "totp") {
        setSecret(data.secret);
        setQr(data.qr);
      } else {
        setEmailHint(data.email_hint);
        setDevOtp(data.dev_otp || null);
      }
      setStep("enrol");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const verifySetup = async (e) => {
    e?.preventDefault?.();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/mfa/setup/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ method, code: code.trim(), mfa_token: setup.mfa_token }),
      });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data?.detail || "Invalid code");
      onSuccess(data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-white shadow-2xl border-0" data-testid="mfa-setup-card">
      <CardContent className="p-7 space-y-5">
        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-[#FFD507]/20 mx-auto flex items-center justify-center mb-3">
            <Shield className="w-7 h-7 text-[#1E3A8A]" />
          </div>
          <h2 className="text-2xl font-heading font-bold text-slate-900">Two-factor authentication required</h2>
          <p className="text-sm text-slate-500 mt-1">
            For security, all <span className="font-medium text-slate-700">{setup.role}</span> accounts must enable MFA before signing in.
          </p>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm" data-testid="mfa-setup-error">{error}</div>}

        {step === "pick" && (
          <div className="space-y-3" data-testid="mfa-method-picker">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Choose a method</p>
            <button
              onClick={() => startSetup("totp")}
              disabled={loading}
              className="w-full border border-slate-200 rounded-xl p-4 hover:border-[#1E3A8A] hover:bg-slate-50 transition text-left flex items-start gap-3"
              data-testid="pick-totp"
            >
              <Smartphone className="w-6 h-6 text-[#1E3A8A] mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-slate-900">Authenticator app <span className="text-xs text-emerald-700 ml-2">Recommended</span></p>
                <p className="text-xs text-slate-500 mt-0.5">Use Google Authenticator, Authy, 1Password, or any TOTP app.</p>
              </div>
            </button>
            <button
              onClick={() => startSetup("email")}
              disabled={loading}
              className="w-full border border-slate-200 rounded-xl p-4 hover:border-[#1E3A8A] hover:bg-slate-50 transition text-left flex items-start gap-3"
              data-testid="pick-email"
            >
              <Mail className="w-6 h-6 text-[#1E3A8A] mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-slate-900">Email one-time code</p>
                <p className="text-xs text-slate-500 mt-0.5">We'll email a 6-digit code to {setup.email} every time you sign in.</p>
              </div>
            </button>
            <button onClick={onCancel} className="text-xs text-slate-500 hover:text-slate-700 mt-2" data-testid="mfa-back-to-login">Use a different account</button>
          </div>
        )}

        {step === "enrol" && method === "totp" && (
          <div className="space-y-4" data-testid="mfa-totp-enrol">
            <div className="flex flex-col items-center bg-slate-50 rounded-xl p-4">
              {qr && <img src={qr} alt="Scan this QR code with your authenticator app" className="w-44 h-44" />}
              <p className="text-xs text-slate-500 mt-3">Scan with your authenticator app.</p>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Can't scan? Enter this secret manually</p>
              <div className="font-mono text-sm text-slate-900 break-all select-all" data-testid="mfa-totp-secret">{secret}</div>
            </div>
            <form onSubmit={verifySetup} className="space-y-3">
              <Label className="text-slate-600 text-sm">Enter the 6-digit code from your app</Label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric"
                maxLength={6}
                placeholder="••••••"
                className="input-light text-center text-2xl tracking-[0.5em] font-mono h-14"
                data-testid="mfa-totp-code"
                autoComplete="one-time-code"
              />
              <Button type="submit" disabled={loading || code.length !== 6} className="w-full btn-primary h-11" data-testid="mfa-totp-confirm">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><KeyRound className="w-4 h-4 mr-1" /> Confirm &amp; sign in</>)}
              </Button>
              <button type="button" onClick={() => { setStep("pick"); setCode(""); }} className="text-xs text-slate-500 hover:text-slate-700">← Choose a different method</button>
            </form>
          </div>
        )}

        {step === "enrol" && method === "email" && (
          <div className="space-y-4" data-testid="mfa-email-enrol">
            <p className="text-sm text-slate-600">
              We sent a 6-digit code to <span className="font-medium text-slate-900">{emailHint || setup.email}</span>.
            </p>
            {devOtp && (
              <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs rounded p-2 text-center" data-testid="dev-otp-setup-banner">
                <span className="font-bold">DEV / PREVIEW MODE</span> — your code is <span className="font-mono font-bold">{devOtp}</span>
              </div>
            )}
            <form onSubmit={verifySetup} className="space-y-3">
              <Label className="text-slate-600 text-sm">Enter the code</Label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric"
                maxLength={6}
                placeholder="••••••"
                className="input-light text-center text-2xl tracking-[0.5em] font-mono h-14"
                data-testid="mfa-email-code"
                autoComplete="one-time-code"
              />
              <Button type="submit" disabled={loading || code.length !== 6} className="w-full btn-primary h-11" data-testid="mfa-email-confirm">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><KeyRound className="w-4 h-4 mr-1" /> Confirm &amp; sign in</>)}
              </Button>
              <button type="button" onClick={() => { setStep("pick"); setCode(""); }} className="text-xs text-slate-500 hover:text-slate-700">← Choose a different method</button>
            </form>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
